// src/components/layout/AuthLayout.jsx
import React from 'react';
import { Outlet, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  useTheme,
  alpha,
  styled,
  useMediaQuery
} from '@mui/material';
import { AutoAwesome as MagicIcon } from '@mui/icons-material';

// Import stores
import useAuthStore from '../../stores/authStore';
import useSettingsStore from '../../stores/settingsStore';
import useUIStore from '../../stores/uiStore';
import logo from '../../assets/images/logo_512.png';

// Import theme toggle component
import ThemeToggle from '../ui/ThemeToggle';

// Styled components for the new design
const GradientTypography = styled(Typography)(({ theme }) => ({
  background: theme.aghrba?.gradients?.goldText || 'linear-gradient(90deg, #f2c526 0%, #FFD859 100%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
  display: 'inline-block',
}));

const LogoBox = styled(Box)(({ theme }) => ({
  width: 50,
  height: 50,
  borderRadius: 14,
  background: `linear-gradient(135deg, ${theme.palette.primary.light}, ${theme.palette.primary.dark})`,
  // opacity:0.1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginRight: 12,
  position: 'relative',
  overflow: 'hidden',
}));

const BackgroundGlow = styled(Box)(({ theme, position }) => ({
  position: 'absolute',
  ...position,
  width: position.width || '500px',
  height: position.height || '500px',
  borderRadius: '50%',
  background: `radial-gradient(circle, ${alpha(theme.palette.primary.light, 0.2)} 0%, transparent 70%)`,
  zIndex: 0
}));

// Auth layout component
const AuthLayout = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints?.down('md') || '(max-width: 960px)');
  const { isAuthenticated } = useAuthStore();
  const { settings } = useSettingsStore();
  // const { magicEffectsEnabled } = useUIStore ? useUIStore() : { magicEffectsEnabled: true };
  
  // Redirect if already authenticated
  React.useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);
  
  return (
    <Box sx={{
      minHeight: '100vh',
      display: 'flex',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Background decorative elements */}
      <BackgroundGlow position={{ top: '10%', left: '-5%' }} />
      <BackgroundGlow position={{ bottom: '-15%', right: '-5%', width: '600px', height: '600px' }} />
      
      {/* Left side - Brand showcase (hidden on mobile) */}
      <Box sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 5,
        position: 'relative',
        zIndex: 2,
        display: { xs: 'none', md: 'flex' }
      }}>
        <Container maxWidth="sm" sx={{ textAlign: 'center' }}>
          {/* Logo */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3.75, }}>
            <LogoBox>
               
                               <img 
                                 src={logo} 
                                 alt="Aghrba Logo" 
                                 style={{ 
                                   height: 24, 
                                 }} 
                               />
            </LogoBox>
            {/* <GradientTypography variant="h4" fontFamily="Playfair Display" fontWeight="bold">
              Aghrba
            </GradientTypography> */}
          </Box>
          
          <Typography variant="h4" fontFamily="Playfair Display" mb={2.5}>
            Discover the Magic
          </Typography>
          
          <Typography variant="body1" color="text.secondary" mb={3.75}>
            Enter the enchanting world of Aghrba, where marketing and technology 
            combine to create extraordinary solutions for your business.
          </Typography>
        </Container>
      </Box>
      
      {/* Right side - Auth content */}
      <Paper sx={{
        flex: { xs: 1, md: 1 },
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        boxShadow: { xs: 'none', md: '-10px 0 30px rgba(0, 0, 0, 0.2)' },
        position: 'relative',
        zIndex: 3,
        borderRadius: 0,
        minHeight: '100vh',
      }}>
        <Container 
          maxWidth="xs" 
          sx={{ 
            py: 5,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            height: '100%',
          }}
        >
          {/* Outlet for auth pages */}
          <Outlet />
        </Container>
      </Paper>
      
      {/* Theme Toggle Button - positioned in the top-right corner */}
      <Box
        sx={{
          position: 'absolute',
          top: 16,
          right: 16,
          zIndex: 10
        }}
      >
        <ThemeToggle size="small" />
      </Box>
      
      {/* Footer */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 16,
          left: 0,
          right: 0,
          textAlign: 'center',
          zIndex: 4,
          display: { xs: 'none', md: 'block' }
        }}
      >
        <Typography variant="body2" color="text.secondary">
          &copy; {new Date().getFullYear()} Aghrba. All rights reserved.
        </Typography>
        <Box sx={{ mt: 1 }}>
          <Typography 
            component={RouterLink} 
            to="/terms" 
            variant="caption" 
            color="text.accent"
            sx={{ textDecoration: 'none', mx: 1 }}
          >
            Terms of Service
          </Typography>
          <Typography 
            component={RouterLink} 
            to="/privacy" 
            variant="caption" 
            color="text.accent"
            sx={{ textDecoration: 'none', mx: 1 }}
          >
            Privacy Policy
          </Typography>
          <Typography 
            component={RouterLink} 
            to="/contact" 
            variant="caption" 
            color="text.accent"
            sx={{ textDecoration: 'none', mx: 1 }}
          >
            Contact Us
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default AuthLayout;